'use client';

import { useEffect, useEffectEvent, useId } from 'react';

const OVERLAY_HISTORY_KEY = '__welloOverlay';

const closingMarkers = new Set<string>();
let pendingBack: { marker: string; landed: Promise<void> } | undefined;
let isPoppingForNavigation = false;

const getCurrentMarker = () =>
  typeof window === 'undefined'
    ? undefined
    : (window.history.state?.[OVERLAY_HISTORY_KEY] as string | undefined);

/** 현재 히스토리 엔트리가 오버레이가 만든 임시 엔트리인지 — 라우트 이동이 이 위를 덮어쓴다. */
export const hasOverlayHistoryEntry = () => getCurrentMarker() !== undefined;

//* 오버레이를 떠나는 라우트 이동 직전에 부른다 — 마커가 남으면 닫힘의 back이 그 이동을 되감는다
export const clearOverlayHistoryEntry = () => {
  if (!hasOverlayHistoryEntry()) {
    return;
  }

  const state = { ...window.history.state };
  delete state[OVERLAY_HISTORY_KEY];
  window.history.replaceState(state, '');
};

//* 마커 엔트리를 되감고 popstate가 도착하면 풀린다 — 그동안 오버레이는 닫히지도 back을 다시 부르지도 않는다
export const popOverlayHistoryEntry = () =>
  new Promise<void>((resolve) => {
    if (!hasOverlayHistoryEntry()) {
      resolve();

      return;
    }

    isPoppingForNavigation = true;
    window.addEventListener(
      'popstate',
      () => {
        isPoppingForNavigation = false;
        resolve();
      },
      { once: true },
    );
    window.history.back();
  });

/**
 * 화면을 덮는 오버레이가 브라우저·앱 뒤로가기로 닫히게 한다.
 * 열릴 때 history 엔트리를 하나 push하고 그 엔트리가 pop되면 close를 부른다.
 * X·backdrop·ESC로 닫힌 경우엔 남은 엔트리를 back으로 소비해 히스토리가 쌓이지 않게 한다 — 닫으며 언마운트되는 조건부 마운트 소비처도 같다.
 *
 * ⚠️ **DS 프리미티브(Modal·BottomDrawer)에는 걸지 않는다** — 세 가지가 깨진다:
 * ① 반응형 분기가 Modal↔Drawer로 갈아탈 때 마커가 두 번 쌓이거나 소실되고
 * ② 컨테이너가 dynamic import로 늦게 마운트해 열린 직후 back이 무방비이며
 * ③ onClose가 optional인 소비처는 pop돼도 닫을 방법이 없다.
 * 그래서 close를 필수 인자로 받는다 — 닫을 수 없는 쪽은 애초에 이 계약을 질 수 없다.
 * 앱의 인라인 오버레이는 `ResponsiveDialog`가 분기 위에서 대신 계약을 진다 — DS Modal 직접 사용·dynamic import 컨테이너만 여는 쪽이 직접 부른다.
 */
export const useOverlayHistory = (opened: boolean, close: () => void) => {
  const id = useId();
  const closeOnPop = useEffectEvent(close);

  useEffect(() => {
    if (!opened) {
      return;
    }

    let openedHref: string | undefined;
    let isCancelled = false;

    const handlePopState = () => {
      //* 내 마커가 그대로면 더 깊이 열린 오버레이가 닫힌 것이다 — 나는 살아 있어야 한다.
      if (getCurrentMarker() === id || isPoppingForNavigation) {
        return;
      }

      closeOnPop();
    };

    const enter = (isHandoff: boolean) => {
      const nextState = { ...window.history.state, [OVERLAY_HISTORY_KEY]: id };

      if (isHandoff) {
        window.history.replaceState(nextState, '');
      } else {
        window.history.pushState(nextState, '');
      }

      openedHref = window.location.href;
      window.addEventListener('popstate', handlePopState);
    };

    const closingMarker = getCurrentMarker();

    //* 같은 커밋에서 닫힌 오버레이의 엔트리를 물려받는다 — 새로 push하면 그쪽의 back이 나를 닫는다
    if (closingMarker !== undefined && closingMarkers.delete(closingMarker)) {
      enter(true);
    } else if (pendingBack && pendingBack.marker === closingMarker) {
      //* 앞서 닫힌 오버레이의 back이 아직 도착 전이다 — 먼저 push하면 그 back이 내 엔트리를 되감는다
      void pendingBack.landed.then(() => {
        if (!isCancelled) {
          enter(false);
        }
      });
    } else {
      enter(false);
    }

    return () => {
      isCancelled = true;
      window.removeEventListener('popstate', handlePopState);

      //* 라우트 이동이면 마커가 이미 지워졌거나 URL이 달라진다 — 되감으면 그 이동 자체가 취소된다.
      if (
        getCurrentMarker() !== id ||
        window.location.href !== openedHref ||
        isPoppingForNavigation
      ) {
        return;
      }

      closingMarkers.add(id);
      queueMicrotask(() => {
        if (!closingMarkers.delete(id) || getCurrentMarker() !== id) {
          return;
        }

        pendingBack = {
          marker: id,
          landed: new Promise((resolve) => {
            window.addEventListener(
              'popstate',
              () => {
                pendingBack = undefined;
                resolve();
              },
              { once: true },
            );
          }),
        };
        window.history.back();
      });
    };
  }, [opened, id]);
};
